/**
 * Alta, edición y baja de estudiantes que funcionan SIN conexión.
 *
 * El patrón es el mismo que el de los pagos, y por la misma razón: se escribe
 * primero en IndexedDB y se encola la operación, de modo que cerrar el navegador o
 * quedarse sin batería no pierda lo capturado. La red viene después.
 *
 * Dos cosas que este módulo NO hace, a propósito:
 *
 *  · No garantiza que el código sea único. Solo puede comprobarlo contra lo que el
 *    dispositivo tiene sincronizado. Si otra caja usó el mismo código mientras esta
 *    estaba sin conexión, el servidor lo detecta al sincronizar y abre un conflicto.
 *  · No fusiona nada. Si hay choque, se conservan las dos versiones y decide una
 *    persona.
 */

import { idempotencyKey, newId, type Instant } from '@senm/domain';
import { db, normalizeForSearch, type LocalStudent } from '../local/db.js';
import { enqueue } from '../sync/engine.js';
import type { StoredSession } from '../local/session.js';

export interface DatosEstudiante {
  code: string;
  firstNames: string;
  lastNames: string;
  birthDate: string;
  guardianName: string;
  guardianPhone: string;
  cycleId: string;
  gradeId?: string | null;
  sectionId?: string | null;
  gradeName?: string | null;
  sectionName?: string | null;
}

export class CodigoDuplicadoLocalError extends Error {
  constructor(codigo: string) {
    super(
      `Ya existe un estudiante con el código "${codigo}" en este dispositivo. ` +
        'Use otro código o revise si ya fue dado de alta.',
    );
    this.name = 'CodigoDuplicadoLocalError';
  }
}

function nombreCompleto(datos: DatosEstudiante): string {
  return `${datos.firstNames.trim()} ${datos.lastNames.trim()}`.replace(/\s+/g, ' ').trim();
}

/**
 * Comprueba el código contra los datos LOCALES. Es una validación temprana para no
 * hacerle perder el viaje al usuario, no una garantía: la autoritativa es del
 * servidor.
 */
export async function codigoDisponibleLocalmente(
  codigo: string,
  excluirId?: string,
): Promise<boolean> {
  const normalizado = codigo.trim().toUpperCase();
  const existentes = await db.students.toArray();
  return !existentes.some(
    (estudiante) =>
      estudiante.code.trim().toUpperCase() === normalizado && estudiante.id !== excluirId,
  );
}

/**
 * Da de alta un estudiante. Funciona con y sin conexión: se guarda localmente y se
 * encola. El identificador se genera aquí, antes de guardar, y es lo que hace que
 * reenviar la operación no cree un segundo estudiante.
 */
export async function crearEstudianteLocal(params: {
  datos: DatosEstudiante;
  session: StoredSession;
  now: Instant;
}): Promise<LocalStudent> {
  const { datos, session, now } = params;

  if (!(await codigoDisponibleLocalmente(datos.code))) {
    throw new CodigoDuplicadoLocalError(datos.code.trim());
  }

  const studentId = newId();
  const fullName = nombreCompleto(datos);

  const local: LocalStudent = {
    id: studentId,
    code: datos.code.trim(),
    fullName,
    searchName: normalizeForSearch(fullName),
    active: true,
    cycleId: datos.cycleId,
    gradeId: datos.gradeId ?? '',
    gradeName: datos.gradeName ?? '',
    sectionId: datos.sectionId ?? '',
    sectionName: datos.sectionName ?? '',
    outstandingMinor: 0,
    creditMinor: 0,
    currency: session.user.currency,
    version: 1,
    updatedAt: now,
  };

  // Escritura local y encolado en la MISMA transacción: o queda todo, o nada. Un
  // estudiante guardado sin su operación en la cola nunca llegaría al servidor.
  await db.transaction('rw', [db.students, db.outbox], async () => {
    await db.students.put(local);
    await enqueue({
      id: studentId,
      type: 'ESTUDIANTE_ALTA',
      idempotencyKey: idempotencyKey(session.user.institutionId, 'ESTUDIANTE_ALTA', studentId),
      payload: {
        studentId,
        code: datos.code.trim(),
        firstNames: datos.firstNames.trim(),
        lastNames: datos.lastNames.trim(),
        birthDate: datos.birthDate,
        guardianName: datos.guardianName.trim(),
        guardianPhone: datos.guardianPhone.trim(),
        cycleId: datos.cycleId,
        assignment:
          datos.gradeId && datos.sectionId
            ? { cycleId: datos.cycleId, gradeId: datos.gradeId, sectionId: datos.sectionId }
            : null,
      },
      status: 'PENDIENTE',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
      nextAttemptAt: null,
      dependsOn: [],
      lastErrorCode: null,
      lastErrorMessage: null,
    });
  });

  return local;
}

/**
 * Edita los datos de un estudiante.
 *
 * `dependsOn` apunta al alta cuando el estudiante todavía no está sincronizado: sin
 * ese orden causal, el servidor podría recibir la edición de alguien que aún no
 * existe.
 */
export async function editarEstudianteLocal(params: {
  studentId: string;
  datos: Partial<DatosEstudiante>;
  expectedVersion: number;
  session: StoredSession;
  now: Instant;
}): Promise<void> {
  const { studentId, datos, expectedVersion, session, now } = params;

  if (datos.code !== undefined && !(await codigoDisponibleLocalmente(datos.code, studentId))) {
    throw new CodigoDuplicadoLocalError(datos.code.trim());
  }

  const altaPendiente = await db.outbox
    .where('id')
    .equals(studentId)
    .filter((entrada) => entrada.type === 'ESTUDIANTE_ALTA' && entrada.status !== 'SINCRONIZADO')
    .first();

  const operationId = newId();
  await db.transaction('rw', [db.students, db.outbox], async () => {
    const actual = await db.students.get(studentId);
    if (actual !== undefined) {
      const fullName =
        datos.firstNames !== undefined || datos.lastNames !== undefined
          ? nombreCompleto({
              ...(datos as DatosEstudiante),
              firstNames: datos.firstNames ?? '',
              lastNames: datos.lastNames ?? '',
            })
          : actual.fullName;
      await db.students.update(studentId, {
        code: datos.code?.trim() ?? actual.code,
        fullName,
        searchName: normalizeForSearch(fullName),
        updatedAt: now,
      });
    }

    await enqueue({
      id: operationId,
      type: 'ESTUDIANTE_EDICION',
      idempotencyKey: idempotencyKey(session.user.institutionId, 'ESTUDIANTE_EDICION', operationId),
      payload: {
        studentId,
        ...(datos.code !== undefined ? { code: datos.code.trim() } : {}),
        ...(datos.firstNames !== undefined ? { firstNames: datos.firstNames.trim() } : {}),
        ...(datos.lastNames !== undefined ? { lastNames: datos.lastNames.trim() } : {}),
        ...(datos.birthDate !== undefined ? { birthDate: datos.birthDate } : {}),
        ...(datos.guardianName !== undefined ? { guardianName: datos.guardianName.trim() } : {}),
        ...(datos.guardianPhone !== undefined ? { guardianPhone: datos.guardianPhone.trim() } : {}),
        expectedVersion,
      },
      status: 'PENDIENTE',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
      nextAttemptAt: null,
      // Orden causal: la edición no puede llegar antes que el alta.
      dependsOn: altaPendiente ? [studentId] : [],
      lastErrorCode: null,
      lastErrorMessage: null,
    });
  });
}

/** Da de baja o reactiva. Conserva cargos, pagos y recibos: solo cambia el estado. */
export async function cambiarEstadoEstudianteLocal(params: {
  studentId: string;
  active: boolean;
  reason: string | null;
  expectedVersion: number;
  session: StoredSession;
  now: Instant;
}): Promise<void> {
  const { studentId, active, reason, expectedVersion, session, now } = params;

  const altaPendiente = await db.outbox
    .where('id')
    .equals(studentId)
    .filter((entrada) => entrada.type === 'ESTUDIANTE_ALTA' && entrada.status !== 'SINCRONIZADO')
    .first();

  const operationId = newId();
  await db.transaction('rw', [db.students, db.outbox], async () => {
    await db.students.update(studentId, { active, updatedAt: now });
    await enqueue({
      id: operationId,
      type: 'ESTUDIANTE_ESTADO',
      idempotencyKey: idempotencyKey(session.user.institutionId, 'ESTUDIANTE_ESTADO', operationId),
      payload: { studentId, active, reason, expectedVersion },
      status: 'PENDIENTE',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
      nextAttemptAt: null,
      dependsOn: altaPendiente ? [studentId] : [],
      lastErrorCode: null,
      lastErrorMessage: null,
    });
  });
}
